import { useState, useCallback } from 'react'
import { setCurrentSelectionId } from 'tripPlanner/reducers/actions'
import type { ExploreItem } from 'tripPlanner/types/common'
import { exploreItemSelectionId, tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import type { ExploreItemMapMarker } from '../utils'
import { mapToExploreMarker } from '../utils'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getPlaceIdToExploreItemMap, getPlaceIdToSavedExploreItemMap, getPlaceIdToSavedItemMap } from 'tripPlanner/utils/explore'
import type { MapItem } from 'tripPlanner/types/tripItem'
import type { PublicMapItem } from 'tripPlanner/types/publicTrip'

const SELECTED_SEARCH_ITEM_ZOOM = 14

export default function useMapSearchState(
  mapRef: React.RefObject<google.maps.Map | undefined>,
  items: Array<MapItem | PublicMapItem>,
  exploreItems: Array<ExploreItem>,
  useCardCarousel: boolean,
) {
  const dispatch = useAppDispatch()

  const [mapSearchItem, _setMapSearchItem] = useState<ExploreItem | undefined>(
    undefined,
  )

  const [mapSearchMarker, setMapSearchMarker] = useState<
    ExploreItemMapMarker | undefined
  >(undefined)

  const savedItemsByPlaceId = getPlaceIdToSavedItemMap(items)
  const savedExploreItemsByPlaceId = getPlaceIdToSavedExploreItemMap(items)
  const exploreItemsByPlaceId = getPlaceIdToExploreItemMap(exploreItems)

  const setMapSearchItem = useCallback((searchItem: ExploreItem | undefined) => {
    if (!searchItem?.place.id) {
      return
    }

    _setMapSearchItem(searchItem)

    // Check if the map searched item is already in the items or explore items.

    // Is it already in the explore items?
    if (exploreItemsByPlaceId.has(searchItem.place.id)) {
      const item = exploreItemsByPlaceId.get(searchItem.place.id)!
      dispatch(setCurrentSelectionId(exploreItemSelectionId(item.id)))

      // card carousel has its own map logic on selection (panToIntoViewIfObstructed)
      if (!useCardCarousel) {
        mapRef.current?.setZoom(SELECTED_SEARCH_ITEM_ZOOM)
        mapRef.current?.panTo(item.place.point)
      }

      setMapSearchMarker(undefined)

      return
    }

    // Is it already a saved explore item?
    if (savedExploreItemsByPlaceId.has(searchItem.place.id)) {
      setMapSearchMarker(undefined)

      const item = savedExploreItemsByPlaceId.get(searchItem.place.id)!
      dispatch(setCurrentSelectionId(tripItemSelectionId(item.id)))
      mapRef.current?.setZoom(SELECTED_SEARCH_ITEM_ZOOM)
      mapRef.current?.panTo(item.place.point)

      return
    }

    if (savedItemsByPlaceId.has(searchItem.place.id)) {
      setMapSearchMarker(undefined)

      const item = savedItemsByPlaceId.get(searchItem.place.id)!
      dispatch(setCurrentSelectionId(tripItemSelectionId(item.id)))
      mapRef.current?.setZoom(SELECTED_SEARCH_ITEM_ZOOM)
      mapRef.current?.panTo(item.place.point)

      return
    }

    // It needs its own marker
    setMapSearchMarker(mapToExploreMarker(searchItem))

    // falling into here because the item saved is not in the explore items.
    if (searchItem) {
      dispatch(setCurrentSelectionId(exploreItemSelectionId(searchItem.id)))
      mapRef.current?.setZoom(SELECTED_SEARCH_ITEM_ZOOM)
      mapRef.current?.panTo(searchItem.place.point)
    }
  }, [dispatch, exploreItemsByPlaceId, mapRef, savedExploreItemsByPlaceId, savedItemsByPlaceId, useCardCarousel])

  const clearMapSearchItem = useCallback(() => {
    if (!mapSearchItem?.place.id) {
      return
    }

    _setMapSearchItem(undefined)
    setMapSearchMarker(undefined)
  }, [mapSearchItem])

  return {
    mapSearchMarker,
    setMapSearchItem,
    clearMapSearchItem,
  }
}
